import {
  Modal as AntModal,
  Input,
  Select as AntSelect,
  Checkbox,
  Row,
  Col,
  Button as AntButton,
  Space,
  Typography,
  Progress,
  Tooltip,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  EyeOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  LockOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
  BankOutlined,
  IdcardOutlined,
  ThunderboltOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { COUNTRY_CODES } from "../utils/phones";

const { Option } = AntSelect;
const { Title, Text } = Typography;

const AddCustomerModal = ({
  open,
  onCancel,
  onSubmit,
  newCustomer,
  setNewCustomer,
  password,
  setPassword,
  isGenerating,
  generatePassword,
  handleNewCustomerChange,
  states,
  errors,
  customerErrors,
  formData,
  setFormData,
  isAddingCustomer,
}) => {
  const [touched, setTouched] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Password strength calculation
  useEffect(() => {
    if (password) {
      let strength = 0;
      if (password.length >= 6) strength += 25;
      if (password.length >= 10) strength += 25;
      if (/[A-Z]/.test(password)) strength += 15;
      if (/[a-z]/.test(password)) strength += 15;
      if (/[0-9]/.test(password)) strength += 10;
      if (/[^A-Za-z0-9]/.test(password)) strength += 10;
      setPasswordStrength(Math.min(strength, 100));
    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return "#ff4d4f";
    if (passwordStrength < 70) return "#faad14";
    return "#52c41a";
  };

  const getPasswordStrengthStatus = () => {
    if (passwordStrength < 40) return "exception";
    if (passwordStrength < 70) return "normal";
    return "success";
  };

  const copyPassword = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    toast.success("Password copied to clipboard!", { autoClose: 2000 });
  };

  // Reset touched on modal close/open
  useEffect(() => {
    if (!open) setTouched({});
  }, [open]);

  /* ================= STYLES ================= */
  const sectionCard = {
    background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
    border: "2px solid #e8ecff",
    borderRadius: "16px",
    marginBottom: "24px",
    borderLeft: "4px solid #667eea",
    transition: "all 0.3s ease",
  };

  const sectionHeader = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "2px solid rgba(102, 126, 234, 0.15)",
  };

  const sectionIcon = {
    width: "44px",
    height: "44px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "20px",
    boxShadow: "0 6px 16px rgba(102, 126, 234, 0.3)",
    flexShrink: 0,
  };

  const sectionTitle = {
    margin: 0,
    fontSize: "17px",
    fontWeight: "700",
    color: "#262626",
  };

  const sectionSubtitle = {
    margin: "2px 0 0 0",
    fontSize: "13px",
    color: "#8c8c8c",
  };

  const inputLabel = {
    fontWeight: 600,
    color: "#262626",
    fontSize: "13px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const gstCheckbox = {
    background: "white",
    border: "2px solid #d9d9d9",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "16px",
    transition: "all 0.3s",
    cursor: "pointer",
  };

  const gstCheckboxChecked = {
    background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 100%)",
    border: "2px solid #667eea",
    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.15)",
  };

  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={920}
      centered
      destroyOnClose
      title={null}
      bodyStyle={{
        padding: 0,
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Modal Header ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "24px 32px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          margin: "-20px -24px 0",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "50px",
            height: "50px",
            background: "rgba(255,255,255,0.22)",
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            color: "white",
          }}
        >
          <UserOutlined />
        </div>
        <div>
          <h4
            style={{
              color: "white",
              margin: 0,
              fontSize: "20px",
              fontWeight: 700,
            }}
          >
            Add New Customer
          </h4>
          <p
            style={{
              color: "rgba(255,255,255,0.85)",
              margin: 0,
              fontSize: "13px",
            }}
          >
            Create a new customer profile to continue order creation
          </p>
        </div>
      </div>

      {/* ── Modal Body ── */}
      <div style={{ padding: "28px 32px 24px", overflowY: "auto", flex: 1 }}>
        <form onSubmit={onSubmit}>
          {/* ══════════ BASIC DETAILS ══════════ */}
          <div style={sectionCard}>
            <div style={{ padding: "20px 24px" }}>
              <div style={sectionHeader}>
                <div style={sectionIcon}>
                  <UserOutlined />
                </div>
                <div>
                  <h5 style={sectionTitle}>Basic Details</h5>
                  <p style={sectionSubtitle}>
                    Enter customer's personal information
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>
                    <UserOutlined style={{ color: "#667eea" }} /> Full Name{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Input
                    size="large"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleNewCustomerChange}
                    onBlur={() => markTouched("name")}
                    placeholder="Enter full name"
                    prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                    status={touched.name && customerErrors.name ? "error" : ""}
                    style={{ borderRadius: "8px" }}
                  />
                  {touched.name && customerErrors.name && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {customerErrors.name}
                    </Text>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>
                    <PhoneOutlined style={{ color: "#667eea" }} /> Mobile Number{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {/* Country code is part of the number now — customers are not
                        India-only, and the code decides how the dialer reaches them. */}
                    <AntSelect
                      size="large"
                      value={newCustomer.country_code || "+91"}
                      onChange={(v) =>
                        setNewCustomer((prev) => ({ ...prev, country_code: v }))
                      }
                      style={{ width: 132, flexShrink: 0 }}
                      showSearch
                      optionFilterProp="search"
                      options={COUNTRY_CODES.map((c) => ({
                        value: c.code,
                        label: `${c.flag} ${c.code}`,
                        search: `${c.code} ${c.label}`,
                      }))}
                    />
                    <Input
                      size="large"
                      name="mobile"
                      value={newCustomer.mobile}
                      onChange={handleNewCustomerChange}
                      onBlur={() => markTouched("mobile")}
                      placeholder="Number without country code"
                      prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                      maxLength={15}
                      status={
                        touched.mobile && customerErrors.mobile ? "error" : ""
                      }
                      style={{ borderRadius: "8px" }}
                    />
                  </div>
                  {touched.mobile && customerErrors.mobile && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {customerErrors.mobile}
                    </Text>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-1">
                  <div style={inputLabel}>
                    <MailOutlined style={{ color: "#667eea" }} /> Email Address{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Input
                    size="large"
                    name="email"
                    type="email"
                    value={newCustomer.email}
                    onChange={handleNewCustomerChange}
                    onBlur={() => markTouched("email")}
                    placeholder="customer@example.com"
                    prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                    status={
                      (touched.email && !newCustomer.email) ||
                      customerErrors?.email
                        ? "error"
                        : ""
                    }
                    style={{ borderRadius: "8px" }}
                    autoComplete="new-email"
                  />
                  {touched.email && !newCustomer.email && (
                    <Text type="danger">Email is required</Text>
                  )}

                  {customerErrors?.email && (
                    <Text type="danger">{customerErrors.email}</Text>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ══════════ LOCATION DETAILS ══════════ */}
          <div style={sectionCard}>
            <div style={{ padding: "20px 24px" }}>
              <div style={sectionHeader}>
                <div style={sectionIcon}>
                  <EnvironmentOutlined />
                </div>
                <div>
                  <h5 style={sectionTitle}>Location Details</h5>
                  <p style={sectionSubtitle}>
                    Provide address and location information
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <div style={inputLabel}>
                    <HomeOutlined style={{ color: "#667eea" }} /> Address{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Input
                    size="large"
                    name="address"
                    value={newCustomer.address}
                    onChange={handleNewCustomerChange}
                    onBlur={() => markTouched("address")}
                    placeholder="Street address, apartment, suite, etc."
                    prefix={<HomeOutlined style={{ color: "#bfbfbf" }} />}
                    status={
                      touched.address && customerErrors.address ? "error" : ""
                    }
                    style={{ borderRadius: "8px" }}
                  />
                  {touched.address && customerErrors.address && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {customerErrors.address}
                    </Text>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>
                    <EnvironmentOutlined style={{ color: "#667eea" }} /> State{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <AntSelect
                    showSearch
                    size="large"
                    placeholder="Select state..."
                    value={formData.state_name || undefined}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, state_name: value }));
                      markTouched("state_name");
                    }}
                    onBlur={() => markTouched("state_name")}
                    optionFilterProp="children"
                    style={{ width: "100%", borderRadius: "8px" }}
                    status={
                      touched.state_name && !formData.state_name ? "error" : ""
                    }
                  >
                    {states.map((state) => (
                      <Option key={state.value} value={state.value}>
                        {state.label}
                      </Option>
                    ))}
                  </AntSelect>
                  {touched.state_name && !formData.state_name && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      State is required
                    </Text>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>
                    <EnvironmentOutlined style={{ color: "#667eea" }} /> Pincode{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Input
                    size="large"
                    value={formData.pincode}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                    onBlur={() => markTouched("pincode")}
                    placeholder="6-digit pincode"
                    prefix={
                      <EnvironmentOutlined style={{ color: "#bfbfbf" }} />
                    }
                    maxLength={6}
                    status={touched.pincode && !formData.pincode ? "error" : ""}
                    style={{ borderRadius: "8px" }}
                  />
                  {touched.pincode && !formData.pincode && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      Pincode is required
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ══════════ SECURITY SETTINGS ══════════ */}
          <div style={sectionCard}>
            <div style={{ padding: "20px 24px" }}>
              <div style={sectionHeader}>
                <div style={sectionIcon}>
                  <SafetyOutlined />
                </div>
                <div>
                  <h5 style={sectionTitle}>Security Settings</h5>
                  <p style={sectionSubtitle}>
                    Set up account password and security
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>
                    <LockOutlined style={{ color: "#667eea" }} /> Password{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Input.Password
                    size="large"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      markTouched("password");
                    }}
                    onBlur={() => markTouched("password")}
                    placeholder="Enter password"
                    prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    status={
                      (touched.password && !password) ||
                      customerErrors?.password
                        ? "error"
                        : ""
                    }
                    style={{ borderRadius: "8px" }}
                    autoComplete="new-password"
                  />

                  {touched.password && !password && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      Password is required
                    </Text>
                  )}

                  {customerErrors?.password && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {customerErrors.password}
                    </Text>
                  )}
                  {password && (
                    <div style={{ marginTop: "10px" }}>
                      <Progress
                        percent={passwordStrength}
                        strokeColor={getPasswordStrengthColor()}
                        status={getPasswordStrengthStatus()}
                        size="small"
                        format={(percent) =>
                          percent < 40
                            ? "Weak"
                            : percent < 70
                              ? "Medium"
                              : "Strong"
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <div style={inputLabel}>Quick Actions</div>
                  <Space.Compact style={{ width: "100%" }}>
                    <Tooltip title="Generate secure password">
                      <AntButton
                        size="large"
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={generatePassword}
                        loading={isGenerating}
                        style={{
                          flex: 1,
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "8px 0 0 8px",
                          fontWeight: 600,
                        }}
                      >
                        Generate Password
                      </AntButton>
                    </Tooltip>
                    <Tooltip title="Copy password">
                      <AntButton
                        size="large"
                        icon={<CopyOutlined />}
                        onClick={copyPassword}
                        disabled={!password}
                        style={{ borderRadius: "0 8px 8px 0" }}
                      />
                    </Tooltip>
                  </Space.Compact>

                  <Alert
                    message="Use a strong password with 8+ characters"
                    type="info"
                    showIcon
                    style={{
                      marginTop: "12px",
                      fontSize: "12px",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ══════════ GST REGISTRATION ══════════ */}
          <div style={sectionCard}>
            <div style={{ padding: "20px 24px" }}>
              <div style={sectionHeader}>
                <div style={sectionIcon}>
                  <FileProtectOutlined />
                </div>
                <div>
                  <h5 style={sectionTitle}>GST Registration</h5>
                  <p style={sectionSubtitle}>
                    Optional GST and company details
                  </p>
                </div>
              </div>

              <div
                style={{
                  ...gstCheckbox,
                  ...(formData.gst_inclusion ? gstCheckboxChecked : {}),
                }}
              >
                <Checkbox
                  checked={formData.gst_inclusion}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      gst_inclusion: e.target.checked,
                    }))
                  }
                  style={{ fontSize: "16px" }}
                >
                  <div style={{ marginLeft: "8px" }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#262626",
                        marginBottom: "2px",
                      }}
                    >
                      <CheckCircleOutlined
                        style={{
                          marginRight: "8px",
                          color: formData.gst_inclusion ? "#667eea" : "#d9d9d9",
                        }}
                      />
                      Customer has GST Registration
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#8c8c8c",
                        margin: 0,
                        marginLeft: "24px",
                      }}
                    >
                      Enable this if the customer is registered for GST
                    </p>
                  </div>
                </Checkbox>
              </div>

              {formData.gst_inclusion && (
                <div className="row" style={{ marginTop: "16px" }}>
                  <div className="col-md-6 mb-3">
                    <div style={inputLabel}>
                      <BankOutlined style={{ color: "#667eea" }} /> Company Name{" "}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </div>
                    <Input
                      size="large"
                      value={formData.company_name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          company_name: e.target.value,
                        }))
                      }
                      onBlur={() => markTouched("company_name")}
                      placeholder="Registered company name"
                      prefix={<BankOutlined style={{ color: "#bfbfbf" }} />}
                      status={
                        touched.company_name && !formData.company_name
                          ? "error"
                          : ""
                      }
                      style={{ borderRadius: "8px" }}
                    />
                    {touched.company_name && !formData.company_name && (
                      <Text
                        type="danger"
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          display: "block",
                        }}
                      >
                        Company name is required
                      </Text>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <div style={inputLabel}>
                      <IdcardOutlined style={{ color: "#667eea" }} /> GST Number{" "}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </div>
                    <Input
                      size="large"
                      value={formData.gstNumber}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          gstNumber: e.target.value.toUpperCase(),
                        }))
                      }
                      onBlur={() => markTouched("gstNumber")}
                      placeholder="e.g. 27ABCDE1234F2Z5"
                      prefix={<IdcardOutlined style={{ color: "#bfbfbf" }} />}
                      maxLength={15}
                      status={
                        touched.gstNumber &&
                        (!formData.gstNumber ||
                          (formData.gstNumber.length === 15 &&
                            !/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/.test(formData.gstNumber)))
                          ? "error"
                          : ""
                      }
                      style={{ borderRadius: "8px" }}
                    />
                    {touched.gstNumber && !formData.gstNumber && (
                      <Text
                        type="danger"
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          display: "block",
                        }}
                      >
                        GST number is required
                      </Text>
                    )}
                    {touched.gstNumber &&
                      formData.gstNumber &&
                      formData.gstNumber.length === 15 &&
                      !/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/.test(formData.gstNumber) && (
                        <Text
                          type="danger"
                          style={{
                            fontSize: "12px",
                            marginTop: "4px",
                            display: "block",
                          }}
                        >
                          Invalid GSTIN format. Expected: 27ABCDE1234F2Z5
                        </Text>
                      )}
                    {touched.gstNumber &&
                      formData.gstNumber &&
                      formData.gstNumber.length > 0 &&
                      formData.gstNumber.length < 15 && (
                        <Text
                          type="warning"
                          style={{
                            fontSize: "12px",
                            marginTop: "4px",
                            display: "block",
                            color: "#faad14",
                          }}
                        >
                          GSTIN must be exactly 15 characters ({formData.gstNumber.length}/15)
                        </Text>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ══════════ ACTION BUTTONS ══════════ */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "20px",
              borderTop: "2px solid #f0f0f0",
            }}
          >
            <AntButton
              size="large"
              onClick={onCancel}
              style={{
                minWidth: "120px",
                borderRadius: "10px",
                fontWeight: 600,
              }}
            >
              Cancel
            </AntButton>
            <AntButton
              type="primary"
              htmlType="submit"
              size="large"
              loading={isAddingCustomer}
              icon={<CheckCircleOutlined />}
              style={{
                minWidth: "180px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                fontWeight: 600,
                borderRadius: "10px",
                boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
              }}
            >
              Add Customer
            </AntButton>
          </div>
        </form>
      </div>
    </AntModal>
  );
};

export default AddCustomerModal;
